/**
 * PWA install + update-available UI.
 *
 *   - Android / Chrome / Edge: catches `beforeinstallprompt` and
 *     surfaces an "Install Lilia" banner. Tapping prompts the OS to
 *     install. Dismissable for the session.
 *   - iOS Safari: there's no install prompt event. We render a one-
 *     time hint card with the "Share → Add to Home Screen" steps,
 *     remembering dismissal across visits.
 *   - All platforms: when the SW reports an update is ready, show a
 *     small toast asking the user to refresh.
 *
 * Mount this once at the top of the SPA (next to AuthProvider).
 * Inert when window.matchMedia('(display-mode: standalone)') is
 * true — already installed; no install prompt needed.
 */
import { useEffect, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { useLocation } from 'react-router';
import { useMobileReader } from '@/hooks/useMobileMode';

const DISMISS_KEY_INSTALL = 'lilia.pwa.install.dismissed';
const DISMISS_KEY_IOS = 'lilia.pwa.ios-hint.dismissed';

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    // iOS Safari
    (window.navigator as { standalone?: boolean }).standalone === true
  );
}

function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || navigator.vendor;
  return /iPad|iPhone|iPod/.test(ua) && !(window as { MSStream?: unknown }).MSStream;
}

export function PWAInstallPrompt() {
  // Not over a document: on a phone the reader's Ask bar and Lilia's change card
  // live at the bottom, where these hints sit. They wait for the documents list.
  const { pathname } = useLocation();
  // A phone shows the home-screen hint in its documents list instead, after the
  // list rather than over it (Olivia, 10 Oct): nothing floats there.
  const phone = useMobileReader();
  const hideHints = pathname.startsWith("/d/") || phone;
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [installVisible, setInstallVisible] = useState(false);
  const [iosHintVisible, setIosHintVisible] = useState(false);

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(swUrl) {
      // eslint-disable-next-line no-console
      console.log('[pwa] service worker registered at', swUrl);
    },
    onRegisterError(error) {
      // eslint-disable-next-line no-console
      console.warn('[pwa] service worker registration failed:', error);
    },
  });

  // Capture beforeinstallprompt (Android Chrome / Edge / desktop Chrome).
  useEffect(() => {
    if (isStandalone()) return;
    if (sessionStorage.getItem(DISMISS_KEY_INSTALL)) return;

    const onBIP = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setInstallVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onBIP);
    return () => window.removeEventListener('beforeinstallprompt', onBIP);
  }, []);

  // iOS one-time install hint.
  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem(DISMISS_KEY_IOS)) return;
    if (!isIOS()) return;
    // Give the SPA a beat before nudging.
    const t = window.setTimeout(() => setIosHintVisible(true), 3000);
    return () => window.clearTimeout(t);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    setInstallVisible(false);
    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    // eslint-disable-next-line no-console
    console.log('[pwa] install choice', choice.outcome);
    setDeferredPrompt(null);
  };

  const dismissInstall = () => {
    sessionStorage.setItem(DISMISS_KEY_INSTALL, '1');
    setInstallVisible(false);
  };
  const dismissIos = () => {
    localStorage.setItem(DISMISS_KEY_IOS, '1');
    setIosHintVisible(false);
  };

  return (
    <>
      {/* Update toast */}
      {needRefresh && (
        <div
          className="fixed left-1/2 z-[70] -translate-x-1/2 rounded-xl px-3 py-2 text-[13px]"
          style={{
            bottom: 'calc(env(safe-area-inset-bottom, 0px) + 80px)',
            background: '#171514',
            color: '#fff',
            boxShadow: '0 8px 24px rgba(0,0,0,.3)',
          }}
          role="status"
        >
          <span className="mr-3">A new version is available.</span>
          <button
            type="button"
            onClick={() => updateServiceWorker(true)}
            className="rounded-md bg-white/15 px-2 py-1 text-[12px] font-semibold hover:bg-white/25"
          >
            Refresh
          </button>
          <button
            type="button"
            onClick={() => setNeedRefresh(false)}
            className="ml-1 px-1 text-white/60"
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      )}

      {/* Android / Chrome install banner */}
      {installVisible && deferredPrompt && !hideHints && (
        <div
          className="fixed inset-x-3 z-[65] flex items-center gap-3 rounded-xl px-3 py-2.5 shadow-lg"
          style={{
            bottom: 'calc(env(safe-area-inset-bottom, 0px) + 80px)',
            background: 'var(--ip-paper, #fff)',
            border: '1px solid var(--ip-rule, #e0ddd6)',
          }}
          role="dialog"
          aria-label="Install Lilia"
        >
          <div className="flex-1">
            <div className="text-[13px] font-semibold" style={{ color: 'var(--ip-ink, #171514)' }}>
              Install Lilia
            </div>
            <div className="text-[12px]" style={{ color: 'var(--ip-ink-3, #6a6560)' }}>
              Add to your home screen for one-tap access and offline reading.
            </div>
          </div>
          <button
            type="button"
            onClick={handleInstall}
            className="rounded-md px-3 py-1.5 text-[12.5px] font-semibold text-white"
            style={{ background: 'var(--ip-accent, #3d2d7a)' }}
          >
            Install
          </button>
          <button
            type="button"
            onClick={dismissInstall}
            className="text-[18px] leading-none"
            style={{ color: 'var(--ip-ink-3, #6a6560)' }}
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      )}

      {/* iOS Add-to-Home-Screen hint */}
      {iosHintVisible && !hideHints && (
        <div
          className="fixed inset-x-3 z-[65] flex items-start gap-3 rounded-xl px-3 py-3 shadow-lg"
          style={{
            bottom: 'calc(env(safe-area-inset-bottom, 0px) + 80px)',
            background: 'var(--ip-paper, #fff)',
            border: '1px solid var(--ip-rule, #e0ddd6)',
          }}
          role="dialog"
          aria-label="Add Lilia to your home screen"
        >
          <div className="flex-1">
            <div className="text-[13px] font-semibold" style={{ color: 'var(--ip-ink, #171514)' }}>
              Add Lilia to your home screen
            </div>
            <div className="text-[12px]" style={{ color: 'var(--ip-ink-3, #6a6560)' }}>
              Tap{' '}
              <span aria-hidden style={{ verticalAlign: 'middle' }}>
                ⎙
              </span>{' '}
              then <em>Add to Home Screen</em> for the full app experience.
            </div>
          </div>
          <button
            type="button"
            onClick={dismissIos}
            className="text-[18px] leading-none"
            style={{ color: 'var(--ip-ink-3, #6a6560)' }}
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      )}
    </>
  );
}
